import { App, Button, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ExistingCompletedScene } from '@/apis/simulation';

const { Text } = Typography;

/** 弹框中最多列出的 code 数，超出以「…」省略并保留总数 */
const MAX_SHOWN = 5;

/** 用户对已生成完成场景的处理选择；cancel = 关闭弹框、放弃本次提交 */
export type ExistingCompletedChoice = 'ignore' | 'ignoreAndJoin' | 'regenerate' | 'cancel';

interface Props {
  /** 非空即打开 */
  items: ExistingCompletedScene[];
  /** 是否已选目标场景集；未选时「忽略并加入集合」置灰 */
  canJoinSet: boolean;
  /** 当前生成模式文案（事故 / 云控）：检查只针对该模式，事故 / 云控是两条独立场景 */
  modeLabel: string;
  onChoose: (choice: ExistingCompletedChoice) => void;
}

/**
 * 批量生成提交前：输入的 case 中有已生成完成（已完成 / 已完成-瑕疵品）的场景时弹出。
 *
 * - 忽略：从输入中弃掉这些 case，其余照常提交；
 * - 忽略并加入集合：弃掉这些 case，并把对应场景加入当前目标场景集；
 * - 重新生成：二次确认（会丢弃原有数据、重置为系统生成）后全部照常提交。
 */
const ExistingCompletedModal: React.FC<Props> = ({ items, canJoinSet, modeLabel, onChoose }) => {
  const { modal } = App.useApp();
  const total = items.length;
  const shown = items.slice(0, MAX_SHOWN);

  const confirmRegenerate = () => {
    modal.confirm({
      title: '确认重新生成？',
      icon: <ExclamationCircleOutlined />,
      content: `将丢弃这 ${total} 个场景的原有数据，重置为系统生成的结果，此操作不可撤销。`,
      okText: '确认重新生成',
      okButtonProps: { danger: true },
      cancelText: '再想想',
      onOk: () => onChoose('regenerate'),
    });
  };

  return (
    <Modal
      open={total > 0}
      title="部分场景已生成完成"
      onCancel={() => onChoose('cancel')}
      maskClosable={false}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={() => onChoose('ignore')}>忽略</Button>
          <Tooltip title={canJoinSet ? undefined : '请先选择目标场景集'}>
            <Button
              type="primary"
              disabled={!canJoinSet}
              onClick={() => onChoose('ignoreAndJoin')}
            >
              忽略并加入集合
            </Button>
          </Tooltip>
          <Button danger onClick={confirmRegenerate}>重新生成</Button>
        </Space>
      }
    >
      <Text>
        以下 <Text strong>{total}</Text> 个{modeLabel}场景已生成完成：
      </Text>
      <div style={{ margin: '8px 0', lineHeight: '28px' }}>
        {shown.map(it => (
          <Tooltip key={it.code} title={`${it.sceneName} · ${it.statusName}`}>
            <Tag color={it.status === 7 ? 'gold' : 'green'}>{it.code}</Tag>
          </Tooltip>
        ))}
        {total > MAX_SHOWN && <Text type="secondary">… 共 {total} 个</Text>}
      </div>
      <Text type="secondary">
        忽略：从输入中移除这些 case；忽略并加入集合：移除并把对应场景加入目标场景集；
        重新生成：丢弃原有数据重新生成。
      </Text>
    </Modal>
  );
};

export default ExistingCompletedModal;
